import type {Address, AddressFormValidation} from "~/types/Address";
import {createDefaultAddress, createDefaultAddressFormValidation} from "~/types/Address";
import type {HouseCustomerForm, HouseCustomerFormValidation} from "~/types/Customer";
import {createDefaultHouseCustomerForm, createDefaultHouseCustomerFormValidation} from "~/types/Customer";

export interface HouseInsuranceCompany {
    id: number | null;
    name: string | null;
    code: string | null;
    logo: string | null;
}

export interface HouseInsuranceOfferRequest {
    house_insurance_detail_id: number | null;
    insurance_company: HouseInsuranceCompany | null;
    valid_from: string;
    pad_series: string | null;
    pad_number: number | null;
    policy_type: OfferType | null;
    token: string | null | undefined;
}

export const createDefaultHouseInsuranceOfferRequest = (): HouseInsuranceOfferRequest => {
    return {
        house_insurance_detail_id: null,
        insurance_company: null,
        valid_from: '',
        pad_series: null,
        pad_number: null,
        policy_type: null,
        token: null,
    }
}

export interface BuildingType {
    id: number | null;
    name: string | null;
}

export interface BuildingHeight {
    id: number | null;
    name: string | null;
}

export interface AddressType {
    id: number | null;
    name: string | null;
}

export interface InsuredSumType {
    id: number | null;
    name: string | null;
}

export interface ResistanceStructure {
    id: number | null;
    name: string | null;
}

export interface GoodsType {
    id: number | null;
    name: string | null;
}

export interface SeismicRisk {
    id: number | null;
    name: string | null;
}

export interface OfferType {
    id: number | null;
    name: string | null;
}

export interface HouseInsuranceForm {
    house_insurance_detail_id: number | null;
    building_type: BuildingType;
    address_type: AddressType;
    insured_sum_type: InsuredSumType;
    seismic_risk: SeismicRisk | null;
    building_height: BuildingHeight | null;
    resistance_structure: ResistanceStructure | null;
    number_of_rooms: number | null;
    amount_insured: number | null;
    surface: number | null;
    goods_list_insured: HouseGoods[] | null;
    is_house_with_seismic_risk: boolean;
    is_system_security_installed: boolean;
    year_of_construction: number | null;
    address: Address;
    identifier: string | null;
    users: HouseCustomerForm[] | null;
    policy_type: OfferType | null;
    pad_series: string | null;
    pad_number: number | null;
    valid_from: string;
    token: string | undefined;
    gdpr_agreement: boolean;
}

export interface ValidationHouseInsuranceFormStatus {
    building_type: boolean;
    address_type: boolean;
    insured_sum_type: boolean;
    seismic_risk: boolean;
    building_height: boolean;
    resistance_structure: boolean;
    number_of_rooms: boolean;
    amount_insured: boolean;
    surface: boolean;
    goods_list_insured: boolean[];
    is_house_with_seismic_risk: boolean;
    is_system_security_installed: boolean;
    year_of_construction: boolean;
    address: AddressFormValidation;
    identifier: boolean;
    users: HouseCustomerFormValidation[];
    policy_type: boolean;
    pad_series: boolean;
    pad_number: boolean;
    valid_from: boolean;
    gdpr_agreement: boolean;
    [key: string]: boolean | boolean[] | HouseCustomerFormValidation[] | AddressFormValidation;
}

export const createDefaultValidationHouseInsuranceFormStatus = (): ValidationHouseInsuranceFormStatus => {
    return {
        building_type: true,
        address_type: true,
        insured_sum_type: false,
        seismic_risk: false,
        building_height: false,
        resistance_structure: false,
        number_of_rooms: false,
        amount_insured: false,
        surface: false,
        goods_list_insured: [],
        is_house_with_seismic_risk: true,
        is_system_security_installed: true,
        year_of_construction: false,
        address: createDefaultAddressFormValidation(),
        users: [
            createDefaultHouseCustomerFormValidation()
        ],
        identifier: false,
        policy_type: true,
        pad_series: true,
        pad_number: true,
        valid_from: true,
        gdpr_agreement: false,
    }
}

export const createDefaultHouseInsuranceForm = (): HouseInsuranceForm => {
    return {
        house_insurance_detail_id: null,
        building_type: {
            id: 1,
            name: "Apartament",
        },
        address_type: {
            id: 0,
            name: "Urban",
        },
        insured_sum_type: {
            id: 0,
            name: "Reala",
        },
        seismic_risk: null,
        building_height: null,
        resistance_structure: null,
        number_of_rooms: null,
        amount_insured: null,
        surface: null,
        goods_list_insured: [],
        is_house_with_seismic_risk: false,
        is_system_security_installed: false,
        year_of_construction: null,
        address: createDefaultAddress(),
        identifier: null,
        users: [
            createDefaultHouseCustomerForm(),
        ],
        policy_type: {
            id: 1,
            name: "PAD",
        },
        pad_series: null,
        pad_number: null,
        valid_from: '',
        token: undefined,
        gdpr_agreement: false,
    }
}

export interface HouseGoods {
    goods_type: GoodsType | null;
    amount_insured: number | null;
}

export const createDefaultHouseGoods = (): HouseGoods => {
    return {
        goods_type: null,
        amount_insured: null,
    }
}

export interface ValidationHouseGoodsStatus {
    goods_type: boolean;
    amount_insured: boolean;
    [key: string]: boolean | boolean[];
}

export const createDefaultValidationHouseGoodsStatus = (): ValidationHouseGoodsStatus => {
    return {
        goods_type: false,
        amount_insured: false,
    }
}

export interface ApiOffersResponse {
    offers: ApiOffer[];
}

export interface ApiOffer {
    id?: number | null;
    type?: string | null;
    code?: string | null;
    request_body?: {
        societate?: string | null;
        produs?: string | null;
        pachet?: string | null;
        credentiale_societate?: {
            cont?: string | null;
            parola?: string | null;
            cod?: string | null;
        } | null;
        oferta?: {
            moneda?: string | null;
            valabilitate?: number | null;
            valabilitate_inceput?: string | null;
            frecventa_de_plata?: string | null;
            emitent?: string | null;
        } | null;
        persoane?: Array<{
            rol?: string | null;
            tip_persoana?: string | null;
            cnp_cui?: string | null;
            nume?: string | null;
            prenume?: string | null;
            persoana_straina?: boolean | null;
            data_nasterii?: string | null;
            sex?: string | null;
            email?: string | null;
            telefon?: string | null;
            expus_politic?: boolean | null;
            nationalitate?: string | null;
            cetatenie?: string | null;
            cota_proprietate?: number | null;
            adresa?: Array<{
                tara?: string | null;
                tip?: string | null;
                tip_strada?: string | null;
                strada?: string | null;
                numar?: string | null;
                bloc?: string | null;
                scara?: string | null;
                apartament?: string | null;
                etaj?: string | null;
                judet?: string | null;
                localitate_denumire?: string | null;
                localitate_siruta?: number | null;
                cod_postal?: string | null;
            }> | null;
        }> | null;
        imobil?: {
            identificare?: string | null;
            an_constructie?: string | null;
            suprafata?: number | null;
            numar_cladiri?: number | null;
            numar_camere?: number | null;
            sistem_alarma?: boolean | null;
            structura_de_rezistenta?: string | null;
            inaltimea_cladirii?: string | null;
            tip_adresa?: string | null;
            tip_imobil?: string | null;
            tip_suma_asigurata_imobil?: string | null;
            adresa?: {
                tara?: string | null;
                tip?: string | null;
                tip_strada?: string | null;
                strada?: string | null;
                numar?: string | null;
                bloc?: string | null;
                scara?: string | null;
                apartament?: string | null;
                etaj?: string | null;
                judet?: string | null;
                localitate_denumire?: string | null;
                localitate_siruta?: number | null;
                cod_postal?: string | null;
            } | null;
        } | null;
        obiecte_asigurate?: Array<{
            tip?: string | null;
            suma_asigurata?: string | number | null;
        }> | null;
        date_suplimentare?: {
            clauze_acoperiri?: {
                cladire?: {
                    flexa?: {
                        cod_fransiza?: string | null;
                    } | null;
                } | null;
                continut?: {
                    flexa?: {
                        cod_fransiza?: string | null;
                    } | null;
                } | null;
                raspundere_civila?: {
                    liability?: {
                        cod_fransiza?: string | null;
                        suma_asigurata?: number | null;
                    } | null;
                } | null;
            } | null;
        } | null;
    } | null;
    insurer_offer_code?: string | null;
    amount?: number | null;
    currency?: string | null;
    amount_to_be_payed?: number | null;
    valid_from?: string | null;
    valid_until?: string | null;
    pid_file_url?: string | null;
    terms_and_conditions_file_url?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    deleted_at?: string | null;
    house_insurance_company_id?: number | null;
    house_insurance_product_id?: number | null;
    house_insurance_package_id?: number | null;
    house_insurance_detail_id?: number | null;
    has_error?: boolean | null;
    error_message?: string | null;
    type_name?: string | null;
    house_insurance_company?: {
        id?: number | null;
        code?: string | null;
        name?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
        deleted_at?: string | null;
        logo?: string | null;
    } | null;
    house_insurance_product?: {
        id?: number | null;
        code?: string | null;
        name?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
        deleted_at?: string | null;
        house_insurance_company_id?: number | null;
    } | null;
    house_insurance_package?: {
        id?: number | null;
        code?: string | null;
        name?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
        deleted_at?: string | null;
        house_insurance_product_id?: number | null;
        it_includes_pad_policy?: boolean | null;
    } | null;
}

export interface HouseInsurancePolicy {
    id: number;
    code: string | null;
    payment_method: string;
    currency: string;
    amount: string;
    payment_proof_document_details: string | null;
    receipt_series: string | null;
    receipt_number: string | null;
    additional_data: string | null;
    request_body: any | null;
    response_body: any | null;
    has_error: number;
    message: string | null;
    insurer_original_response: any | null;
    ws_policy: any | null;
    series: string | null;
    number: string | null;
    valid_from: string;
    valid_until: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    house_insurance_offer_id: number;
    user_id: number;
    is_sent: boolean;
}
